/**
 * Custom hook for handling page data loading and caching
 */
import { useState, useEffect, useTransition } from 'react';
import { getCachedData, setCachedData } from '@/lib/utils/apiCache';
import { getDeviceUnlockHomePage } from '@/store/sagas/serverApis/deviceUnlockNew';
import { DeviceUnlockResponse } from '@/store/sagas/types/deviceUnlock';
import { log } from '@/lib/utils/logger';
import { BRAND_CONSTANTS } from '@/lib/constants/appConstants';
import { isSpanishURL } from '@/lib/utils/brandUtils';
import { useMountedState } from './useMountedState';

export const usePageData = (brand: string = BRAND_CONSTANTS.DEFAULT_BRAND) => {
  const [pageData, setPageData] = useState<DeviceUnlockResponse | null>(null);
  const [isPending, startTransition] = useTransition();
  const { createSafeSetter } = useMountedState();

  // Determine locale so that Spanish and English AEM content are cached separately
  const locale = isSpanishURL() ? 'es' : 'en';

  // Create safe state setters
  const safeSetPageData = createSafeSetter(setPageData);

  useEffect(() => {
    const loadHomePageData = async () => {
      try {
        // Check cache first - use brand + locale in cache key for locale-specific caching
        const cacheKey = `home-page-${brand}-${locale}`;
        const cachedData = getCachedData(cacheKey);
        
        if (cachedData && typeof cachedData === 'object') {
          log.debug('Home page [Cache Hit]');
          startTransition(() => {
            safeSetPageData(cachedData as DeviceUnlockResponse);
          });
          return;
        }
        
        // Fetch from API if not cached - wrap in transition for better UX
        startTransition(async () => {
          try {
            const data = await getDeviceUnlockHomePage(brand);
            log.debug('Home page API response:', data);
            setCachedData(cacheKey, data);
            safeSetPageData(data);
          } catch (error) {
            log.error('Error loading home page data:', error);
          }
        });
      } catch (error) {
        log.error('Error in loadHomePageData:', error);
      }
    };

    setPageData(null); // Reset page data when brand or locale changes
    loadHomePageData();
  }, [brand, locale]); // Re-run when brand or locale changes

  return { 
    pageData, 
    loading: isPending || pageData === null, // Loading when transitioning or no data
    isPending // Expose isPending for more granular control
  };
};
